/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {isBottomSheetTableView} from '@ion/dls/src/components/sidesheet/helpers';
import {useIsDesktop} from '@ion/dls/src/hooks/useMedia';
import {setSideSheetStatus} from '@/store/actions/nav';
import {
  initializeOperationalLayers,
  setFindNearbyPopup
} from '@/store/actions/tracking';
import {sideSheetSelector} from '@/store/selectors/nav-selectors';
import {
  isLayerListUpdateSelector,
  operationalLayersSelector,
  sideSheetViewSelector
} from '@/store/selectors/tracking-selectors';

/**
 * The useOperationalLayerProps hook is used for sharing props to Operational Layers
 * 
 * ```typescript
 * 
 *   import useOperationalLayerProps from './hooks/useOperationalLayerProps';
 * 
 *   const operationalLayerProps = useOperationalLayerProps();
 * 
 *   // operationalLayerProps
 * 
 * ```
 */
export const useOperationalLayerProps = () => {
  const dispatch          = useDispatch();
  const isDesktop         = useIsDesktop();
  const sideSheetStatus   = useSelector(sideSheetSelector);
  const sideSheetView     = useSelector(sideSheetViewSelector);
  const isLayerListUpdate = useSelector(isLayerListUpdateSelector);
  const operationalLayers = useSelector(operationalLayersSelector);

  const getOperationalLayers = useCallback(function(map: __esri.Map) {
    dispatch(initializeOperationalLayers(map) as any);
  }, []);

  const onFindNearby = useCallback(function() {
    if (!sideSheetStatus.desktopSideSheetOpen) {
      dispatch(setFindNearbyPopup(true));
      if (!isBottomSheetTableView(sideSheetView)) {
        dispatch(setSideSheetStatus({
          desktopSideSheetOpen : true,
          mobileSideSheetOpen  : !isDesktop
        }));
      }
    }
  }, [isDesktop, sideSheetView]);

  const operationalLayersProps = useMemo(() => {
    return {
      initializeOperationalLayers : getOperationalLayers,
      findNearby                  : onFindNearby,
      operationalLayers,
      isLayerListUpdate
    };
  }, [
    operationalLayers,
    isLayerListUpdate,
    getOperationalLayers
  ]);

  return operationalLayersProps;
};

export default useOperationalLayerProps;
